import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import {
  Box3,
  Group,
  Mesh,
  type Material,
  MeshStandardMaterial,
  TextureLoader,
  type Texture,
  MathUtils,
  Vector3,
} from 'three'
import { config } from '../config'
import { useInputStore } from '../store/inputStore'
import { useHandStore } from '../store/handStore'
import { useSceneStore } from '../store/sceneStore'
import { useAppStore } from '../store/appStore'
import { cubeWorldPosRef } from './cubeWorldPos'

function useFaceTextures() {
  const [textures, setTextures] = useState<(Texture | null)[]>([null, null, null, null, null, null])
  useEffect(() => {
    const loader = new TextureLoader()
    const next: (Texture | null)[] = [null, null, null, null, null, null]
    let alive = true
    Promise.allSettled(
      [1, 2, 3, 4, 5, 6].map(
        (i) =>
          new Promise<Texture>((resolve, reject) => {
            loader.load(config.assets.paths.faceTexture(i), resolve, undefined, reject)
          }),
      ),
    ).then((results) => {
      if (!alive) return
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') next[i] = r.value
      })
      setTextures([...next])
    })
    return () => {
      alive = false
    }
  }, [])
  return textures
}

function useOptionalGLTF(): Group | null {
  const [scene, setScene] = useState<Group | null>(null)
  const setLoaded = useSceneStore((s) => s.setCubeModelLoaded)
  const setError = useSceneStore((s) => s.setCubeModelError)
  useEffect(() => {
    const loader = new GLTFLoader()
    let cancelled = false
    fetch(config.assets.paths.cubeModel, { method: 'HEAD' })
      .then((res) => {
        if (!res.ok) throw new Error('no model')
        return new Promise<Group>((resolve, reject) => {
          loader.load(
            config.assets.paths.cubeModel,
            (gltf) => resolve(gltf.scene),
            undefined,
            reject,
          )
        })
      })
      .then((g) => {
        if (cancelled) return
        // 自动 fit：把模型最大边缩放到 config.cube.size，再把中心移到原点
        const box = new Box3().setFromObject(g)
        const sizeVec = new Vector3()
        const centerVec = new Vector3()
        box.getSize(sizeVec)
        box.getCenter(centerVec)
        const maxDim = Math.max(sizeVec.x, sizeVec.y, sizeVec.z) || 1
        const s = config.cube.size / maxDim
        g.scale.setScalar(s)
        g.position.sub(centerVec.multiplyScalar(s))
        setScene(g)
        setLoaded(true)
      })
      .catch(() => {
        if (cancelled) return
        setError(true)
      })
    return () => {
      cancelled = true
    }
  }, [setLoaded, setError])
  return scene
}

/** 透明 pass + 最高 renderOrder，确保立方体永远画在胶片半圆柱之后 */
function forceOnTop(root: Group | Mesh | null) {
  if (!root) return
  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return
    const mesh = obj as Mesh
    if (!mesh.visible) return
    mesh.renderOrder = 200
    const m = mesh.material
    const apply = (mat: Material) => {
      mat.transparent = true
      if ('opacity' in mat) (mat as { opacity: number }).opacity = 1
      mat.depthTest = false
      mat.depthWrite = true
      mat.needsUpdate = true
    }
    if (Array.isArray(m)) m.forEach(apply)
    else apply(m)
  })
}

export function Cube() {
  const groupRef = useRef<Group>(null)
  const meshRef = useRef<Mesh>(null)
  const faceTexs = useFaceTextures()
  const gltfScene = useOptionalGLTF()
  const { camera, raycaster, pointer } = useThree()

  const lastReleaseRef = useRef<number | null>(null)
  const releasedAtRef = useRef<number | null>(null)
  const releaseStartPosRef = useRef<Vector3>(new Vector3())
  const dragLatchedRef = useRef(false)
  const lastResetCounterRef = useRef(0)
  const lastReleaseCounterRef = useRef(0)

  const materials = useMemo(
    () =>
      faceTexs.map(
        (t, i) =>
          new MeshStandardMaterial({
            map: t ?? null,
            color: t ? 0xffffff : ['#b8835a', '#7a6f63', '#d8cdb6', '#4f9b6b', '#1a1614', '#f5efe2'][i],
            roughness: 0.6,
            metalness: 0.05,
          }),
      ),
    [faceTexs],
  )

  useEffect(() => {
    forceOnTop(groupRef.current)
  }, [gltfScene, materials])

  useEffect(() => {
    const dom = (document.querySelector('canvas') as HTMLCanvasElement | null) ?? document.body

    let lastX = 0
    let lastY = 0

    function onDown(e: PointerEvent) {
      if (!meshRef.current) return
      raycaster.setFromCamera(pointer, camera)
      const hits = raycaster.intersectObject(meshRef.current, true)
      if (hits.length > 0) {
        dragLatchedRef.current = true
        useInputStore.getState().setDragging(true)
        useInputStore.getState().setSpin(0, 0)
        lastX = e.clientX
        lastY = e.clientY
        dom.setPointerCapture?.(e.pointerId)
      }
    }
    function onMove(e: PointerEvent) {
      if (!dragLatchedRef.current) return
      const dx = e.clientX - lastX
      const dy = e.clientY - lastY
      lastX = e.clientX
      lastY = e.clientY
      useInputStore.getState().addDrag(dx, dy)
    }
    function onUp(e: PointerEvent) {
      if (!dragLatchedRef.current) return
      dragLatchedRef.current = false
      const { dx, dy } = useInputStore.getState().drag
      useInputStore.getState().setDragging(false)
      useInputStore.getState().setSpin(dx * 0.05, dy * 0.05)
      dom.releasePointerCapture?.(e.pointerId)
    }

    dom.addEventListener('pointerdown', onDown)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      dom.removeEventListener('pointerdown', onDown)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [camera, raycaster, pointer])

  useFrame(() => {
    const g = groupRef.current
    if (!g) return

    const hand = useHandStore.getState()
    const input = useInputStore.getState()

    if (hand.phase === 'pinching') {
      const t = config.cube.followLerp
      g.position.x = MathUtils.lerp(g.position.x, hand.targetX, t)
      g.position.y = MathUtils.lerp(g.position.y, hand.targetY, t)
      g.position.z = MathUtils.lerp(g.position.z, hand.targetZ, t)
      releasedAtRef.current = null
      lastReleaseRef.current = performance.now()
    } else {
      const justReleased = lastReleaseRef.current !== null
      if (justReleased && releasedAtRef.current === null) {
        releasedAtRef.current = performance.now()
        releaseStartPosRef.current.copy(g.position)
        lastReleaseRef.current = null
      }
      if (releasedAtRef.current !== null) {
        const elapsed = performance.now() - releasedAtRef.current
        if (elapsed > config.cube.returnDelayMs) {
          const k = Math.min(
            1,
            (elapsed - config.cube.returnDelayMs) / config.cube.returnDurationMs,
          )
          const ease = 1 - Math.pow(1 - k, 3)
          g.position.x = MathUtils.lerp(releaseStartPosRef.current.x, 0, ease)
          g.position.y = MathUtils.lerp(releaseStartPosRef.current.y, 0, ease)
          g.position.z = MathUtils.lerp(releaseStartPosRef.current.z, 0, ease)
          if (k >= 1) releasedAtRef.current = null
        }
      }
    }

    const b = config.cube.bounds
    g.position.x = MathUtils.clamp(g.position.x, b.x[0], b.x[1])
    g.position.y = MathUtils.clamp(g.position.y, b.y[0], b.y[1])
    g.position.z = MathUtils.clamp(g.position.z, b.z[0], b.z[1])

    // 方向键移动：按住时每帧平移（速度 ~2.5 m/s @ 60fps）
    const ARROW_SPEED = 0.042
    const ak = input.arrowKeys
    if (ak.left || ak.right || ak.up || ak.down) {
      const b = config.cube.bounds
      const dx = (ak.right ? 1 : 0) - (ak.left ? 1 : 0)
      const dy = (ak.up ? 1 : 0) - (ak.down ? 1 : 0)
      g.position.x = MathUtils.clamp(g.position.x + dx * ARROW_SPEED, b.x[0], b.x[1])
      g.position.y = MathUtils.clamp(g.position.y + dy * ARROW_SPEED, b.y[0], b.y[1])
    }

    if (input.drag.isDragging) {
      g.rotation.y += input.drag.dx * config.cube.dragRotateSensitivity
      g.rotation.x += input.drag.dy * config.cube.dragRotateSensitivity
      useInputStore.setState((s) => ({ drag: { ...s.drag, dx: 0, dy: 0 } }))
    } else if (input.spin.vx !== 0 || input.spin.vy !== 0) {
      g.rotation.y += input.spin.vx * config.cube.dragRotateSensitivity
      g.rotation.x += input.spin.vy * config.cube.dragRotateSensitivity
      input.decaySpin(config.input.cubeInertiaDecayPerFrame)
      if (Math.abs(input.spin.vx) < 0.01 && Math.abs(input.spin.vy) < 0.01) {
        input.setSpin(0, 0)
      }
    } else if (hand.phase !== 'pinching') {
      g.rotation.y += config.cube.autoRotateSpeed
    }

    if (input.resetCubeRequested !== lastResetCounterRef.current) {
      lastResetCounterRef.current = input.resetCubeRequested
      g.position.set(0, 0, 0)
      g.rotation.set(0, 0, 0)
      releasedAtRef.current = null
      lastReleaseRef.current = null
    }
    if (input.releaseCubeRequested !== lastReleaseCounterRef.current) {
      lastReleaseCounterRef.current = input.releaseCubeRequested
      useHandStore.getState().release()
    }

    // 暴露世界位置（用于 DropBox 命中检测 + HandTracker 抓取偏移）
    g.getWorldPosition(cubeWorldPosRef.current)

    // 成功阶段：cube 缩小消失；返回阶段：恢复
    const phase = useAppStore.getState().phase
    const targetScale =
      phase === 'shrinking' || phase === 'curtain' || phase === 'done' ? 0 : 1
    const sLerp = phase === 'shrinking' ? 0.05 : 0.18
    g.scale.x = MathUtils.lerp(g.scale.x, targetScale, sLerp)
    g.scale.y = MathUtils.lerp(g.scale.y, targetScale, sLerp)
    g.scale.z = MathUtils.lerp(g.scale.z, targetScale, sLerp)
  })

  return (
    <group ref={groupRef}>
      {gltfScene ? (
        <primitive object={gltfScene} />
      ) : (
        <mesh ref={meshRef} castShadow={false} receiveShadow={false}>
          <boxGeometry args={[config.cube.size, config.cube.size, config.cube.size]} />
          {materials.map((mat, i) => (
            <primitive key={i} object={mat} attach={`material-${i}`} />
          ))}
        </mesh>
      )}
      {gltfScene && (
        <mesh ref={meshRef} visible={false}>
          <boxGeometry args={[config.cube.size, config.cube.size, config.cube.size]} />
          <meshBasicMaterial transparent opacity={0} />
        </mesh>
      )}
    </group>
  )
}
